import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PROGRAMS, IMG } from "@/lib/brand";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const pascal = (s) => s.split("-").map((x) => x.charAt(0).toUpperCase() + x.slice(1)).join("");

const BADGES = ["First Adventure", "Creative Explorer", "Science Explorer", "Reading Explorer", "Team Player", "Kindness Badge", "Sports Explorer", "Nature Explorer", "Bermuda Explorer", "Five-Day Streak"];

export default function Program() {
  const [live, setLive] = useState(false);
  useEffect(() => { api.get("/public/settings").then((r) => setLive(!!r.data.registration_live)).catch(() => {}); }, []);
  return (
    <div className="bg-navy min-h-screen text-white">
      <Navbar />
      <section className="relative pt-32 pb-16 grain">
        <div className="absolute inset-0 opacity-20"><img src={IMG.arch} className="w-full h-full object-cover" alt="" /><div className="absolute inset-0 bg-navy/80" /></div>
        <div className="relative max-w-5xl mx-auto px-4 text-center">
          <span className="text-turquoise font-bold tracking-[0.3em] text-sm">THE EXPEDITION</span>
          <h1 className="font-display font-black text-4xl sm:text-6xl mt-3">WHAT HAPPENS BEYOND THE ROCK?</h1>
          <p className="text-white/70 mt-4 max-w-2xl mx-auto text-lg">Eight worlds of discovery run every afternoon from 3:00–6:00 PM. Explorers move between zones led by our adventure guides.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PROGRAMS.map((p, i) => {
            const Icon = Icons[pascal(p.icon)] || Icons.Compass;
            return (
              <motion.div key={p.key} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                whileHover={{ y: -6 }} className="glass border border-white/10 rounded-2xl p-6 hover:border-turquoise/50 transition-colors" data-testid={`program-full-${p.key}`}>
                <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: p.color + "22" }}>
                  <Icon className="w-7 h-7" style={{ color: p.color }} />
                </div>
                <h3 className="font-display font-bold text-lg">{p.title}</h3>
                <p className="text-white/60 text-sm mt-2">{p.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Badges */}
      <section className="bg-ocean py-20 grain">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <Icons.Award className="w-12 h-12 text-gold mx-auto mb-3" />
          <h2 className="font-display font-black text-3xl sm:text-4xl">COLLECT ADVENTURE BADGES</h2>
          <p className="text-white/70 mt-3">Explorers earn digital badges as they discover, create and grow. More badges unlock over time.</p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            {BADGES.map((b) => (
              <span key={b} className="glass border border-gold/30 rounded-full px-4 py-2 text-sm font-semibold text-gold" data-testid={`badge-chip-${b.toLowerCase().replace(/\s/g, "-")}`}>{b}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Add-ons */}
      <section className="bg-navy py-20">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <h2 className="font-display font-black text-3xl sm:text-4xl">HELPFUL ADD-ONS</h2>
          <p className="text-white/70 mt-3">Customize your explorer's day with optional extras at booking.</p>
          <div className="flex justify-center mt-8">
            {[["Homework Completed", "notebook-pen", "We make sure homework is finished before pickup."]].map(([t, ic, d]) => {
              const Icon = Icons[pascal(ic)] || Icons.Plus;
              return (
                <div key={t} className="glass border border-turquoise/20 rounded-2xl p-6 max-w-sm w-full" data-testid={`addon-${t.toLowerCase().replace(/\s/g, "-")}`}>
                  <Icon className="w-8 h-8 text-turquoise mx-auto mb-3" />
                  <h3 className="font-display font-bold">{t}</h3>
                  <p className="text-white/60 text-sm mt-1">{d}</p>
                </div>
              );
            })}
          </div>
          <Link to={live ? "/register" : "/join"}><Button className="mt-10 bg-gold hover:bg-gold/90 text-navy font-bold rounded-full px-8" data-testid="program-register-btn">{live ? "Pre-Register Your Explorer" : "Join the Mailing List"}</Button></Link>
        </div>
      </section>
      {/* NEED A LIFT */}
      <section className="bg-ocean py-16" data-testid="transport-copy-section">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <span className="text-gold font-bold tracking-[0.25em] text-sm">NEED A LIFT?</span>
          <h2 className="font-display font-black text-3xl sm:text-4xl mt-2">WE'LL MAKE THE JOURNEY EASIER</h2>
          <p className="text-white/70 mt-3">One child. One trip. $12 each way. Choose transportation only on the days you need it.</p>
          <div className="grid sm:grid-cols-3 gap-4 mt-8 text-left">
            {[["🚐", "School Pickup", "$12 per child"], ["🏠", "After-School Drop-Off", "$12 per child"], ["↔️", "Both Ways", "$24 per child"]].map(([e, t, p]) => (
              <div key={t} className="glass border border-turquoise/20 rounded-2xl p-5">
                <div className="text-3xl">{e}</div>
                <h3 className="font-display font-bold mt-2">{t}</h3>
                <p className="text-turquoise font-bold">{p}</p>
              </div>
            ))}
          </div>
          <p className="text-white/50 text-xs mt-4">Transportation is subject to route and space availability.</p>
        </div>
      </section>
      <Footer />
    </div>
  );
}
